import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { toWords } from 'number-to-words';

import { useAudioStore } from '../../stores/useAudioStore';

import type { UseMutateFunction, UseMutationOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

const MODEL_ID = 'eleven_flash_v2_5';
const VOICE_ID = 'PT4nqlKZfc06VW1BuClj';
const API_KEY = import.meta.env.VITE_ELEVENLABS_API_KEY || 'sk_0dbcddabdfa287f3f6e95e06ae3cff63f6de17f1cca9bcf4';

const client = axios.create({
  baseURL: 'https://api.elevenlabs.io/v1/text-to-speech/',
  headers: {
    'Content-Type': 'application/json',
    'xi-api-key': API_KEY,
  },
});

type MessagePayload = string;

export type MutationOptionsType<TData, TVariables> = Omit<
  UseMutationOptions<TData, AxiosError, TVariables>,
  'mutationFn'
>;

export type AssistantChatMutationType = UseMutateFunction<ArrayBuffer, AxiosError<unknown>, MessagePayload, unknown>;

// TODO: figure out why this method is not creating the audio file
export const useTextToSpeech = (options?: MutationOptionsType<ArrayBuffer, MessagePayload>) => {
  const playAudio = useAudioStore.use.playAudio();
  return useMutation<ArrayBuffer, AxiosError, MessagePayload>({
    mutationFn: async (text): Promise<ArrayBuffer> => {
      const url = `${VOICE_ID}`;

      const response = await client.post<ArrayBuffer>(
        url,
        JSON.stringify({
          text: normalizeText(text),
          model_id: MODEL_ID,
          voice_settings: {
            stability: 0.5,
            similarity_boost: 0.75,
          },
        }),
      );
      return response.data;
    },
    onMutate: (text) => {
      console.log('Converting TTS:', text);
    },
    onError: (error) => {
      console.error('Error generating speech:', error);
    },
    onSuccess: (data) => {
      playAudio(data);
    },
    ...options,
  });
};

// Be sure to install the number-to-words library before running this code

function normalizeText(text: string): string {
  return (
    text
      // Convert monetary values (e.g., "$1000" → "one thousand dollars", "£1000" → "one thousand pounds")
      .replace(/([$£€¥])(\d+(?:,\d{3})*(?:\.\d{2})?)/g, (_, currency, num) => {
        // Remove commas before parsing
        const numWithoutCommas = num.replace(/,/g, '');

        const currencyMap: { [key: string]: string } = {
          $: 'dollars',
          '£': 'pounds',
          '€': 'euros',
          '¥': 'yen',
        };

        // Check for decimal points to handle cents
        if (numWithoutCommas.includes('.')) {
          const [dollars, cents] = numWithoutCommas.split('.');
          return `${toWords(Number.parseInt(dollars))} ${currencyMap[currency] || 'currency'}${
            cents ? ` and ${toWords(Number.parseInt(cents))} cents` : ''
          }`;
        }

        // Handle whole numbers
        return `${toWords(Number.parseInt(numWithoutCommas))} ${currencyMap[currency] || 'currency'}`;
      })

      // Convert phone numbers (e.g., "555-555-5555" → "five five five, five five five, five five five five")
      .replace(/(\d{3})-(\d{3})-(\d{4})/g, (_, p1, p2, p3) => {
        return `${spellOutDigits(p1)}, ${spellOutDigits(p2)}, ${spellOutDigits(p3)}`;
      })
  );
}

// Helper function to spell out individual digits as words (for phone numbers)
function spellOutDigits(num: string): string {
  return num
    .split('')
    .map((digit) => toWords(Number.parseInt(digit)))
    .join(' ');
}

// ElevenLabs API functions
export const generateSpeech = async (
  text: string,
  voiceId: string = 'kdmDKE6EkgrWrrykO9Qt', // Default to "Sarah" voice
  apiKey: string = API_KEY,
): Promise<ArrayBuffer> => {
  try {
    const url = `https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'xi-api-key': apiKey,
      },
      body: JSON.stringify({
        text: normalizeText(text),

        model_id: 'eleven_flash_v2_5',
        output_format: 'mp3_22050_32',
        voice_settings: {
          stability: 0.7,
          similarity_boost: 0.75,
          speed: 1.1,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`ElevenLabs API error: ${response.status}`);
    }

    return await response.arrayBuffer();
  } catch (error) {
    console.error('Error generating speech:', error);
    throw error;
  }
};
